import {Component, NgZone, OnDestroy, OnInit} from '@angular/core';
import {Router, RouterLink} from '@angular/router';
import {CartService, LineItem} from '../cart.service';
import {BehaviorSubject, Observable, of, Subscription, switchMap, zip} from 'rxjs';
import {Cart, CartStatus, PaymentMethod} from '../Cart';
import {catchError, filter, first, mergeMap, tap} from 'rxjs/operators';
import {UccpConfiguration, UccpService} from '../../payment/uccp.service';
import {CartItemComponent, CartItemProcessingStatus} from '../cart-item/cart-item.component';
import {LicenseType, Messages, ProductService, User, UserService} from '@uofu-uss-aux/software-catalog-ng-core';
import {NotificationService} from '@uofu-uss/angular-util';
import {clone} from 'lodash-es';
import {AsyncPipe, NgForOf, NgIf} from '@angular/common';
import {TotalComponent} from '../total/total.component';
import {SelectPaymentMethodComponent} from '../select-payment-method/select-payment-method.component';
import {DepartmentIdComponent} from '../department-id/department-id.component';
import {CommentsComponent} from '../comments/comments.component';
import {MakePaymentComponent} from '../../payment/make-payment/make-payment.component';

@Component({
  selector: 'app-shopping-cart',
  templateUrl: './shopping-cart.component.html',
  styleUrls: ['./shopping-cart.component.scss'],
  standalone: true,
  imports: [
    AsyncPipe,
    NgForOf,
    CartItemComponent,
    NgIf,
    TotalComponent,
    SelectPaymentMethodComponent,
    DepartmentIdComponent,
    CommentsComponent,
    MakePaymentComponent,
    RouterLink
  ]
})
export class ShoppingCartComponent implements OnInit, OnDestroy {

  static readonly cartLoadMessage = 'Unable to load cart or other related data.';

  submitPayment = false;
  uccpConfig: UccpConfiguration;
  // mutex for if cart items are processing.
  processingCount = 0;
  updatingCart = false;
  checkoutPending = false;
  purchaseOrder = false;
  cartChanges$ = new BehaviorSubject<{ deptId: string, valid: boolean, comments: string }>
  ({deptId: '', valid: true, comments: ''});

  private subscription: Subscription;
  #cart: Cart;
  #requiresDepartmentId: boolean;
  user: User;
  lineItems: LineItem[] = [];

  constructor(private readonly cartService: CartService,
              private readonly productService: ProductService,
              private readonly userService: UserService,
              private readonly uccpService: UccpService,
              private readonly router: Router,
              private readonly ngZone: NgZone,
              private readonly notificationService: NotificationService) {
  }

  ngOnInit(): void {
    this.subscription = this.userService.observable
      .pipe(filter(user => !!user))
      .subscribe(user => {
        this.user = user;
        this.cartChanges$.next({
          deptId: this.cartChanges$.value.deptId || user.deptId,
          valid: true,
          comments: this.cartChanges$.value.comments
        });
      });

    const lineItemObserver = {
      next: items => {
        this.lineItems = items.slice();
        this.#requiresDepartmentId = this.cartContainsUniversityLicenses(this.lineItems);
      },
      error: () => this.notificationService.addError(ShoppingCartComponent.cartLoadMessage),
    };

    this.subscription.add(this.cartService.observable
      .pipe(
        filter(cart => !!cart),
        tap(cart => {
          this.#cart = cart;
          this.cartChanges$.next({
            deptId: cart.deptId || this.cartChanges$.value.deptId,
            valid: true,
            comments: cart.items.length ? (cart.comments || this.cartChanges$.value.comments) : '',
          });
        }),
        switchMap(cart => this.cartService.fetchLineItems(cart)))
      .subscribe(lineItemObserver));

    this.subscription.add(this.cartChanges$.pipe(
      filter(() => !!this.#cart && !!this.user),
      filter((changes) => changes.valid &&
        (this.cart.deptId !== changes.deptId || this.cart.comments !== changes.comments)),
      tap(() => this.updatingCart = true),
      switchMap((changes) => this.updateCartWith(this.cart, changes.deptId, changes.comments)),
      catchError(() => of(this.#cart)),
    ).subscribe(next => {
      this.cartService.value = next;
      this.updatingCart = false;
    }));
  }

  itemUpdated(index: number, existing: LineItem, updated: LineItem) {
    if (!updated) {
      this.lineItems.splice(index, 1);
      this.#requiresDepartmentId = this.cartContainsUniversityLicenses(this.lineItems);
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  checkout(cart: Cart) {
    this.checkoutPending = true;
    const paymentMethod = this.purchaseOrder ? PaymentMethod.UShopPurchaseOrder : PaymentMethod.CreditCard;
    const observer = {
      next: ([updated, current, uccpConfig]) => this.checkoutCartSuccess(updated, current, uccpConfig),
      error: this.checkoutCartError,
    };
    const cartObs = this.updateCartWith(cart, this.cartChanges$.value.deptId, this.cartChanges$.value.comments);
    return cartObs
      .pipe(
        first(),
        tap(given => this.cartService.value = given),
        mergeMap(given => this.cartService.checkout(given, paymentMethod)),
        mergeMap(updated => zip(of(updated), this.fetchCurrent(), this.uccpService.fetchUccpConfig(updated.id))))
      .subscribe(observer);
  }

  fetchCurrent(): Observable<Cart> {
    return this.cartService.fetchCurrentShoppingCart(this.user?.unid)
      .pipe(catchError((err) => {
        this.notificationService.addError(Messages.LOAD_CART_MSG);
        return of(null);
      }));
  }

  private updateCartWith(cart: Cart, deptId: string, comments: string): Observable<Cart> {
    console.debug(`Updating cart with dept ${cart.deptId} to ${deptId}`);
    const clonedCart = clone(cart);
    clonedCart.deptId = deptId;
    clonedCart.comments = comments;
    return this.cartService.updateCart(clonedCart);
  }

  private readonly checkoutCartError = (errorResponse) => {
    this.checkoutPending = false;
    const body = Messages.validationMsg(errorResponse, 'Unable to complete checkout');
    this.notificationService.addError({
      title: 'Error',
      body,
    });
  }

  private readonly checkoutCartSuccess = (checkedOut: Cart, current: Cart, uccpConfig: UccpConfiguration) => {
    this.checkoutPending = false;
    this.cartService.value = current;
    this.ngZone.run(() => {
      if (checkedOut.status === CartStatus.Purchased || checkedOut.status === CartStatus.POPending) {
        this.cartService.value = current;
        this.router.navigate(['order-summary'], {queryParams: {ID: checkedOut.id}});
      } else {
        this.uccpConfig = uccpConfig;
        this.submitPayment = true;
        this.#cart = checkedOut;
      }
    });
  }

  get processing() {
    return this.processingCount !== 0 || this.updatingCart || this.checkoutPending;
  }

  cartItemProcessingCallback(status: CartItemProcessingStatus) {
    const increment = status === CartItemProcessingStatus.Processing ? 1 : -1;
    this.processingCount = this.processingCount + increment;
  }

  get cart() {
    return this.#cart;
  }

  get hasItems() {
    return this.lineItems.length > 0;
  }

  setPurchaseOrder(purchaseOrder: boolean) {
    console.debug(`Updating payment type to use purchase order with value of ${purchaseOrder}`);
    this.purchaseOrder = purchaseOrder;
  }

  updateDeptId(valid: boolean, value: string) {
    this.cartChanges$.next({
      deptId: value,
      valid,
      comments: this.cartChanges$.value.comments
    });
  }

  updateComments(value: string) {
    this.cartChanges$.next({
      deptId: this.cartChanges$.value.deptId,
      valid: this.cartChanges$.value.valid,
      comments: value
    });
  }

  cartContainsUniversityLicenses(lineItems: ReadonlyArray<LineItem>): boolean {
    const item = lineItems?.length && lineItems[0];
    return !!item && this.productService.findLicenseFromOffering(item.product, item.offering)?.type === LicenseType.University;
  }

  get requiresDepartmentId(): boolean {
    return this.#requiresDepartmentId;
  }

}
